import { Check, Building2, Calendar, FileText, IndianRupee, Clock, MapPin, User, Briefcase, Phone, Mail, Globe, CheckCircle2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import ConsultancyBillingSchedule from '@/components/boq/ConsultancyBillingSchedule';

/**
 * BOQ Summary Dashboard Component
 * 
 * Renders a professional BOQ summary page with:
 * - Header with company branding
 * - Project overview panel
 * - Commercial snapshot (client view)
 * - Payment structure
 * - Scope highlights
 * - Important notes
 * 
 * Design: Charcoal (#1A1A1A) + White + Champagne Gold theme
 */
export default function BOQSummaryDashboard({ data, viewType = 'client', worksheetId, publicToken }) {
  if (!data) return null;

  const {
    header,
    company,
    project_overview,
    commercial_snapshot,
    payment_structure,
    scope_highlights,
    important_notes,
    vendor_scope,
    vendor_commercial,
    terms_and_conditions,
    status
  } = data;
  
  // Download Summary PDF handler
  const handleDownloadPDF = () => {
    if (publicToken) {
      // Public view - use public endpoint
      window.open(`${API}/public/boq/${publicToken}/summary-pdf`, '_blank');
    } else if (worksheetId) {
      // Internal view
      window.open(`${API}/boq-worksheets/${worksheetId}/export/summary-pdf?export_role=${viewType}`, '_blank');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹ 0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount).replace('₹', '₹ ');
  };

  return (
    <div className="bg-[#FAFAF8] min-h-screen" data-testid="boq-summary-dashboard">
      {/* Header Section */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-screen-2xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-stone-900 tracking-tight">BOQ Summary</h1>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-stone-500">{header?.project_name}</p>
                {commercial_snapshot?.value_mode === 'actual' && (
                  <span
                    className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-300"
                    data-testid="boq-summary-actual-badge"
                    title="Figures shown reflect Actual (Site) quantities & rates"
                  >
                    Actual (Site)
                  </span>
                )}
                {commercial_snapshot?.value_mode === 'estimated' && (
                  <span
                    className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300"
                    data-testid="boq-summary-estimated-badge"
                    title="Figures shown reflect Estimated (Design) quantities & rates"
                  >
                    Estimated (Design)
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* iter-359e: Download PDF hidden on PUBLIC share views (client + vendor
                  publish URLs). Owners still get it inside the authenticated app. */}
              {worksheetId && !publicToken && (
                <Button
                  onClick={handleDownloadPDF}
                  className="bg-stone-900 text-white hover:bg-stone-800"
                  size="sm"
                  data-testid="download-summary-pdf-public"
                >
                  <Download size={14} className="mr-2" />
                  Download PDF
                </Button>
              )}
              {company?.logo_url && (
                <div className="flex items-center gap-3">
                  <img 
                    src={company.logo_url} 
                    alt={company.name} 
                    className="h-12 w-auto object-contain"
                  />
                  <div className="text-right">
                    <p className="font-semibold text-stone-800 text-sm">{company.name?.toUpperCase()}</p>
                    <p className="text-xs text-stone-500">{company.tagline}</p>
                  </div>
                </div>
              )}
              {!company?.logo_url && company?.name && (
                <div className="text-right">
                  <p className="font-bold text-stone-800">{company.name?.toUpperCase()}</p>
                  <p className="text-xs text-stone-500">{company.tagline}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-6">
        {/* Project Overview Panel */}
        <div className="rounded-xl overflow-hidden shadow-sm" data-testid="project-overview-panel">
          <div className="bg-[#1A1A1A] px-4 py-3">
            <h2 className="text-white font-semibold text-sm tracking-wide">Project Overview</h2>
          </div>
          <div className="bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-stone-100">
              {/* Left Column */}
              <div className="divide-y divide-stone-100">
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Client Name</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{project_overview?.client_name || vendor_scope?.vendor_name}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Project Name</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{project_overview?.project_name || header?.project_name}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Site Location</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{project_overview?.site_location || '-'}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">BOQ Number</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{header?.boq_number}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Valid Until</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{header?.valid_until || '-'}</div>
                </div>
              </div>
              {/* Right Column */}
              <div className="divide-y divide-stone-100">
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Project Type</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{project_overview?.project_type || '-'}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Service Model</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{project_overview?.service_model || '-'}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">BOQ Number</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{header?.boq_number}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Valid Until</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{header?.valid_until || '-'}</div>
                </div>
                <div className="flex">
                  <div className="w-1/2 px-4 py-3 bg-stone-50 text-xs font-medium text-stone-600">Prepared By</div>
                  <div className="w-1/2 px-4 py-3 text-sm text-stone-900">{header?.prepared_by}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Commercial Snapshot - CLIENT VIEW ONLY */}
        {viewType === 'client' && commercial_snapshot && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Financial Summary */}
            <div className="rounded-xl overflow-hidden shadow-sm" data-testid="commercial-snapshot">
              <div className="bg-[#1A1A1A] px-4 py-3">
                <h2 className="text-white font-semibold text-sm tracking-wide">Commercial Snapshot</h2>
              </div>
              <div className="bg-white divide-y divide-stone-100">
                <div className="flex justify-between px-4 py-3">
                  <span className="text-sm text-stone-600">Basic Total</span>
                  <span className="text-sm font-semibold text-stone-900">{formatCurrency(commercial_snapshot.total_boq_value)}</span>
                </div>
                <div className="flex justify-between px-4 py-3 bg-stone-50">
                  <span className="text-sm text-stone-600">GST ({commercial_snapshot.gst_percentage}%)</span>
                  <span className="text-sm font-semibold text-stone-900">{formatCurrency(commercial_snapshot.gst_amount)}</span>
                </div>
                {/* DISCOUNT ROW - ARCHITECTURAL FIX */}
                {(commercial_snapshot.discount_amount > 0 || commercial_snapshot.discount_percentage > 0) && (
                  <div className="flex justify-between px-4 py-3 bg-green-50">
                    <span className="text-sm text-green-700">
                      Discount {commercial_snapshot.discount_type === 'percentage' ? `(${commercial_snapshot.discount_percentage}%)` : ''}
                    </span>
                    <span className="text-sm font-semibold text-green-700">-{formatCurrency(commercial_snapshot.discount_amount)}</span>
                  </div>
                )}
                {/* Overhead Costs - use backend-calculated amounts */}
                {(() => {
                  const oh = data.overheads || {};
                  const freight = commercial_snapshot.overhead_freight || 0;
                  const labour = commercial_snapshot.overhead_labour || 0;
                  const consult = commercial_snapshot.overhead_consultancy || 0;
                  return (
                    <>
                      {freight > 0 && (
                        <div className="flex justify-between px-4 py-2 bg-stone-50">
                          <span className="text-sm text-stone-600">Freight ({oh.freight_pct}%)</span>
                          <span className="text-sm font-semibold text-stone-700">+{formatCurrency(freight)}</span>
                        </div>
                      )}
                      {labour > 0 && (
                        <div className="flex justify-between px-4 py-2">
                          <span className="text-sm text-stone-600">Labour Handling ({oh.labour_handling_pct}%)</span>
                          <span className="text-sm font-semibold text-stone-700">+{formatCurrency(labour)}</span>
                        </div>
                      )}
                      {consult > 0 && (
                        <div className="flex justify-between px-4 py-2 bg-stone-50">
                          <span className="text-sm text-stone-600">Consultancy ({oh.consultancy_pct}%)</span>
                          <span className="text-sm font-semibold text-stone-700">+{formatCurrency(consult)}</span>
                        </div>
                      )}
                      <div className="flex justify-between px-4 py-4 bg-gradient-to-r from-amber-700 to-amber-600">
                        <span className="text-sm font-semibold text-white">Grand Total</span>
                        <span className="text-lg font-bold text-white">{formatCurrency(commercial_snapshot.grand_total)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Right: Metrics */}
            <div className="rounded-xl overflow-hidden shadow-sm bg-[#1A1A1A]" data-testid="boq-metrics">
              <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-300">Total No. of Categories</span>
                  <span className="text-2xl font-bold text-white">{commercial_snapshot.total_categories}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-300">Line Items</span>
                  <span className="text-2xl font-bold text-white">{commercial_snapshot.total_line_items}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-stone-300">Estimated Duration</span>
                  <span className="text-2xl font-bold text-white">{commercial_snapshot.estimated_duration_days || '-'} Days</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary Icons Row */}
        {viewType === 'client' && commercial_snapshot && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-around">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-stone-600" />
                <span className="text-sm font-medium text-stone-700">{commercial_snapshot.total_categories} Categories</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText size={18} className="text-stone-600" />
                <span className="text-sm font-medium text-stone-700">{commercial_snapshot.total_line_items} Line items</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-stone-600" />
                <span className="text-sm font-medium text-stone-700">Estimated Du. {commercial_snapshot.estimated_duration_days || '-'} Days</span>
              </div>
            </div>
          </div>
        )}

        {/* VENDOR VIEW - Scope Summary */}
        {viewType === 'vendor' && vendor_scope && (
          <div className="rounded-xl overflow-hidden shadow-sm" data-testid="vendor-scope-panel">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <h2 className="text-white font-semibold text-sm tracking-wide">Vendor Scope Snapshot</h2>
            </div>
            <div className="bg-white divide-y divide-stone-100">
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-stone-600">Vendor Name</span>
                <span className="text-sm font-semibold text-stone-900">{vendor_scope.vendor_name}</span>
              </div>
              <div className="flex justify-between px-4 py-3 bg-stone-50">
                <span className="text-sm text-stone-600">Project Name</span>
                <span className="text-sm font-semibold text-stone-900">{vendor_scope.project_name}</span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-stone-600">Total Line Items Assigned</span>
                <span className="text-sm font-semibold text-stone-900">{vendor_scope.total_line_items}</span>
              </div>
              <div className="flex justify-between px-4 py-3 bg-stone-50">
                <span className="text-sm text-stone-600">Total Quantity</span>
                <span className="text-sm font-semibold text-stone-900">{vendor_scope.total_quantity}</span>
              </div>
            </div>
          </div>
        )}

        {/* VENDOR VIEW - Commercial Summary */}
        {viewType === 'vendor' && vendor_commercial && (
          <div className="rounded-xl overflow-hidden shadow-sm" data-testid="vendor-commercial-panel">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <h2 className="text-white font-semibold text-sm tracking-wide">Vendor Commercial Summary</h2>
            </div>
            <div className="bg-white divide-y divide-stone-100">
              <div className="flex justify-between px-4 py-4 bg-gradient-to-r from-amber-700 to-amber-600">
                <span className="text-sm font-semibold text-white">Total Scope Value</span>
                <span className="text-lg font-bold text-white">{formatCurrency(vendor_commercial.total_scope_value)}</span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-stone-600">Payment Terms</span>
                <span className="text-sm font-semibold text-stone-900">{vendor_commercial.payment_terms}</span>
              </div>
              <div className="flex justify-between px-4 py-3 bg-stone-50">
                <span className="text-sm text-stone-600">Acceptance Status</span>
                <span className={`text-sm font-semibold ${vendor_commercial.acceptance_status ? 'text-green-600' : 'text-amber-600'}`}>
                  {vendor_commercial.acceptance_status ? 'Accepted' : 'Pending'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Payment Structure & Scope Highlights - CLIENT VIEW */}
        {viewType === 'client' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Structure */}
            {payment_structure && (
              <div className="rounded-xl overflow-hidden shadow-sm" data-testid="payment-structure">
                <div className="bg-[#1A1A1A] px-4 py-3">
                  <h2 className="text-white font-semibold text-sm tracking-wide">Payment Structure</h2>
                </div>
                <div className="bg-white divide-y divide-stone-100">
                  {Object.entries(payment_structure).map(([key, slab], idx) => (
                    <div key={key} className={`flex justify-between px-4 py-3 ${idx % 2 === 1 ? 'bg-stone-50' : ''}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-amber-700 font-bold text-sm">{slab?.percentage}%</span>
                        <span className="text-sm text-stone-600">{slab?.milestone_name || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </div>
                      <span className="text-sm font-semibold text-stone-900">{formatCurrency(slab?.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scope Highlights */}
            {scope_highlights && scope_highlights.length > 0 && (
              <div className="rounded-xl overflow-hidden shadow-sm" data-testid="scope-highlights">
                <div className="bg-[#1A1A1A] px-4 py-3">
                  <h2 className="text-white font-semibold text-sm tracking-wide">Scope Highlights</h2>
                </div>
                <div className="bg-white p-4">
                  <ul className="space-y-2">
                    {scope_highlights.map((category, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <Check size={16} className="text-amber-600" />
                        <span className="text-sm text-stone-700">{category}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Important Notes */}
        {important_notes && important_notes.length > 0 && (
          <div className="rounded-xl overflow-hidden shadow-sm" data-testid="important-notes">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <h2 className="text-white font-semibold text-sm tracking-wide">Important Notes</h2>
            </div>
            <div className="bg-white p-4">
              <ul className="space-y-2">
                {important_notes.map((note, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-stone-600">{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Terms & Conditions - Generated from Task Descriptions */}
        {terms_and_conditions?.generated && terms_and_conditions?.sections?.length > 0 && (
          <div className="rounded-xl overflow-hidden shadow-sm" data-testid="terms-and-conditions">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <h2 className="text-white font-semibold text-sm tracking-wide">Terms & Conditions</h2>
            </div>
            <div className="bg-white p-4 space-y-4">
              {terms_and_conditions.sections.map((section, index) => (
                <div key={index} className="pb-3 border-b border-stone-100 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-stone-800 text-sm mb-1">{section.title}</h3>
                  <p className="text-sm text-stone-600">{section.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Plain text Terms & Conditions (from AI generation in Edit BOQ) */}
        {data.terms_conditions && (
          <div className="rounded-xl overflow-hidden shadow-sm" data-testid="custom-terms-conditions">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <h2 className="text-white font-semibold text-sm tracking-wide">Terms & Conditions</h2>
            </div>
            <div className="bg-white p-4">
              <div className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{data.terms_conditions}</div>
            </div>
          </div>
        )}

        {/* iter-386u-bt — Universal Consultancy Billing Legend */}
        {/* iter-386u-bu — Interactive 5-stage milestone toggle */}
        {viewType !== 'vendor' && (
          <ConsultancyBillingSchedule
            schedule={data?.consultancy_billing_schedule}
            stage={data?.consultancy_stage}
            fee={commercial_snapshot?.overhead_consultancy}
            basicTotal={commercial_snapshot?.total_boq_value}
            publicToken={publicToken}
          />
        )}

        {/* Status Indicator */}
        {status && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">Status</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                status.client_accepted || status.vendor_accepted 
                  ? 'bg-green-100 text-green-700' 
                  : status.token_received 
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-amber-100 text-amber-700'
              }`}>
                {status.label}
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="bg-[#1A1A1A] rounded-xl p-4">
          <div className="flex items-center justify-center gap-6 text-xs text-stone-400">
            {company?.website && (
              <div className="flex items-center gap-1">
                <Globe size={14} />
                <span>{company.website}</span>
              </div>
            )}
            {company?.email && (
              <div className="flex items-center gap-1">
                <Mail size={14} />
                <span>{company.email}</span>
              </div>
            )}
            {company?.phone && (
              <div className="flex items-center gap-1">
                <Phone size={14} />
                <span>{company.phone}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
